import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { isNil } from 'lodash';
import { ModalComponent } from 'mbs-ui-kit';
import { Subject, combineLatest } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Schedule } from 'wizards/common/schedule.class';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { HoursOrMinutesItems } from 'wizards/models/schedule-advanced-models';
import {
  daysOfWeekEnumArray,
  getDefaultFullSchedule,
  getDefaultIncrementalSchedule,
  weekNumberEnumArray
} from 'wizards/steps/schedule-step/NBF/schedule-step.component.utils';

@UntilDestroy()
@Component({
  selector: 'nbf-schedule-modal-component',
  templateUrl: './schedule-modal.component.html'
})
export class NBFScheduleModalComponent implements OnInit {
  public form: UntypedFormGroup;
  public title: string;
  public preset: { weekdays: boolean; everyday: boolean } = { weekdays: false, everyday: false };
  public week: boolean[] = [false, false, false, false, false, false, false];
  public readonly hoursOrMinutesItems = HoursOrMinutesItems;
  public readonly daysOfWeekEnumArray = daysOfWeekEnumArray;
  public readonly weekNumberEnumArray = weekNumberEnumArray;
  public readonly daysShort = this.i18nextService.t('shared:daysShort', { returnObjects: true });
  public readonly elementSelectors = {
    id: {
      dailyRadio: 'NBF-Schedule-Modal-Daily-Radio',
      monthlyRadio: 'NBF-Schedule-Modal-Monthly-Radio',
      presetsBlock: 'NBF-Schedule-Modal-presets-block',
      presetWeekdaysButton: 'NBF-Schedule-Modal-presets-weekdays',
      presetEverydayButton: 'NBF-Schedule-Modal-presets-everyday',
      daysBlock: 'NBF-Schedule-Modal-days-block',
      dayId: (i) => `NBF-Schedule-Modal-day-${i}`,
      occurrenceBlock: 'NBF-Schedule-Modal-occurrence-block',
      occurrenceSelect: 'NBF-Schedule-Modal-occurrence-select',
      calendarDayBlock: 'NBF-Schedule-Modal-calendar-day-block',
      daysSelectBlock: 'NBF-Schedule-Modal-days-select-block',
      timeRadioBlock: 'NBF-Schedule-Modal-time-radio-block',
      timeTooltip: 'NBF-Schedule-Modal-time-tooltip',
      timeSpecificBlock: 'NBF-Schedule-Modal-time-specific-block',
      timeSpecificRadio: 'NBF-Schedule-Modal-time-specific-radio',
      timeSpecificTimepicker: 'NBF-Schedule-Modal-time-specific-timepicker',
      timeSeveralBlock1: 'NBF-Schedule-Modal-time-several-block-1',
      timeSeveralBlock2: 'NBF-Schedule-Modal-time-several-block-2',
      timeSeveralRadio: 'NBF-Schedule-Modal-time-several-radio',
      timeSeveralEveryNumber: 'NBF-Schedule-Modal-time-several-every-number',
      timeSeveralPeriod: 'NBF-Schedule-Modal-time-several-period',
      timeSeveralTime1: 'NBF-Schedule-Modal-time-several-time-1',
      timeSeveralTime2: 'NBF-Schedule-Modal-time-several-time-2',
      repeatEveryNumber: 'NBF-Schedule-Modal-repeat-every-number',
      repeatEveryWeeks: 'NBF-Schedule-Modal-repeat-every-weeks',
      repeatEveryMonths: 'NBF-Schedule-Modal-repeat-every-months',
      saveButton: 'NBF-Additional-Modal-save-button',
      cancelButton: 'NBF-Additional-Modal-cancel-button'
    }
  };

  get isUnix(): boolean {
    return !!this.baseModal && !!this.baseModal.data && this.baseModal.data.isUnix;
  }

  public get isSimple(): boolean {
    return this.mode === 'simple';
  }

  // Unused in component now
  public get isAdvanced(): boolean {
    return this.mode === 'advanced';
  }

  public get isFull(): boolean {
    return this.mode === 'full';
  }

  public isFullEqualIncremental = false;

  public get isDaily(): boolean {
    return this.form?.get('recurringPeriod')?.value === 'Daily';
  }

  public get isMonthly(): boolean {
    return this.form?.get('recurringPeriod')?.value === 'Monthly';
  }

  public get isDayOfMonth(): boolean {
    return this.form?.get('occurrence')?.value === -1;
  }

  public get isCountableDayOfMonth(): boolean {
    return this.form?.get('occurrence')?.value !== null && !this.isDayOfMonth;
  }

  public get isSpecificTime(): boolean {
    return this.form?.get('dailyFreqPeriodOption')?.value === 'OccursAt';
  }

  private mode: 'simple' | 'advanced' | 'full';
  private incrementalSchedule: Schedule;
  private scheduleChanged = new Subject<boolean>();

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  constructor(private i18nextService: I18NextService) {}

  ngOnInit() {
    this.initData();
    this.initForm();
    this.fullData();
  }

  setFrequency(type: 'Daily' | 'Monthly'): void {
    if (type === 'Monthly') {
      this.form.get('dailyFreqPeriodOption').setValue('OccursAt');

      if (isNil(this.form.value.occurrence)) {
        this.form.get('occurrence').setValue(0);
        this.form.get('dayOfWeek').setValue(3);
      }
    }
  }

  setPreset(type: 'weekdays' | 'everyday'): void {
    if (type === 'weekdays') {
      this.form.get('weeklyDayOfWeek').setValue([false, true, true, true, true, true, false]);
      this.preset = { weekdays: true, everyday: false };
    }

    if (type === 'everyday') {
      this.form.get('weeklyDayOfWeek').setValue([true, true, true, true, true, true, true]);
      this.preset = { weekdays: false, everyday: true };
    }
  }

  setDay(day: number): void {
    this.form.get('weeklyDayOfWeek').setValue(this.week.map((value, i) => (day === i ? !value : value)));
  }

  saveClickHandler(): void {
    if (!this.form.valid) {
      return;
    }

    this.baseModal.save({ data: this.form.value });
  }

  cancelClickHandler(): void {
    this.baseModal.close();
  }

  dailyPeriodChangeHandler(event: 'OccursEvery' | 'OccursAt'): void {
    if (event === 'OccursEvery' && isNil(this.form.value.occursEveryCount)) {
      this.form.get('occursEveryCount').setValue(1);
    }
  }

  private initData(): void {
    this.mode = this.baseModal?.data?.mode;

    if (this.isFull) {
      const isIncrementalEnabled = this.baseModal?.data?.incrementalSchedule.enabled;
      const isDaily = this.baseModal?.data?.incrementalSchedule.recurringPeriod === 'Daily';

      this.incrementalSchedule = isIncrementalEnabled
        ? new Schedule(
            isDaily
              ? {
                  type: 'Daily',
                  days: this.baseModal?.data?.incrementalSchedule.weeklyDayOfWeek || []
                }
              : {
                  type: 'Monthly',
                  occurrence: this.baseModal?.data?.incrementalSchedule.occurrence,
                  dayOfWeek: this.baseModal?.data?.incrementalSchedule.dayOfWeek,
                  dayOfMonth: this.baseModal?.data?.incrementalSchedule.dayOfMonthCount
                }
          )
        : null;

      this.scheduleChanged.pipe(untilDestroyed(this), debounceTime(100)).subscribe(() => this.detectFullAndIncrementalEquality());
    }
  }

  private fullData(): void {
    const data = this.baseModal?.data?.schedule;

    this.title = this.i18nextService.t(`wizards:schedule.scheduleModal.title.${this.mode}`); // TODO: Test

    if (!data) {
      return void this.form.reset(data);
    }

    if (!data.dayOfMonthCount) {
      data.dayOfMonthCount = 1;
    }

    this.form.reset(data);

    StepsHelpers.updateDaysOfWeek(data.weeklyDayOfWeek, this.form, this.daysOfWeekEnumArray);
  }

  private initForm() {
    this.form = new UntypedFormGroup(this.isFull ? getDefaultFullSchedule() : getDefaultIncrementalSchedule());

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.week = value.weeklyDayOfWeek;

      this.detectPreset();

      if (this.isFull) {
        this.scheduleChanged.next(true);
      }
    });

    combineLatest([this.form.get('recurringPeriod').valueChanges, this.form.get('weeklyDayOfWeek').valueChanges]).subscribe(() =>
      this.allDaysSelectedValidator()
    );

    this.scheduleChanged.next(true);
  }

  private allDaysSelectedValidator(): void {
    let hasDay;

    if (this.isDaily) {
      hasDay = Array.from(this.form.get('weeklyDayOfWeek').value).some((day) => !!day);
    }

    if (this.isDaily && !hasDay) {
      return void this.form.get('weeklyDayOfWeek').setErrors({ emptyDays: true });
    }

    this.form.get('weeklyDayOfWeek').setErrors(null);
  }

  /**
   * We must compare same params
   * For Daily mode: weeklyDayOfWeek
   * For Monthly mode: occurrence + occurrence params
   * @private
   * @return {null}
   */
  private detectFullAndIncrementalEquality(): void {
    if (!this.isFull || !this.incrementalSchedule) {
      return void delete this.isFullEqualIncremental;
    }

    const isDaily = this.form.get('recurringPeriod').value === 'Daily';
    const currentSchedule = new Schedule(
      isDaily
        ? {
            type: 'Daily',
            days: this.form.get('weeklyDayOfWeek').value
          }
        : {
            type: 'Monthly',
            occurrence: this.form.get('occurrence').value,
            dayOfWeek: this.form.get('dayOfWeek').value,
            dayOfMonth: this.form.get('dayOfMonthCount').value
          }
    );

    if (this.incrementalSchedule.match(currentSchedule)) {
      return void (this.isFullEqualIncremental = true);
    }

    delete this.isFullEqualIncremental;
  }

  private detectPreset(): void {
    let isWeekdays = false;
    let isEveryday = false;
    const summary = (this.form.get('weeklyDayOfWeek').value || []).map((value, i) => (value ? String(i) : '')).join('');

    if (summary === '12345') isWeekdays = true;
    if (summary === '0123456') isEveryday = true;

    this.preset = { weekdays: isEveryday ? false : isWeekdays, everyday: isEveryday };
  }
}
